import { useState } from "react";
import { useTranslation } from "react-i18next";

const FORM_URL = "https://readdy.ai/api/form/d70292tt4s7j0tv5ilm0";
const FORMSPREE_URL = "https://formspree.io/f/mojpoooo";
const WHATSAPP_DR =
	"https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20una%20demo%20gratis.";

export default function Contact() {
	const [status, setStatus] = useState<
		"idle" | "sending" | "success" | "error"
	>("idle");
	const [charCount, setCharCount] = useState(0);
	const { t } = useTranslation();

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const form = e.currentTarget;
		const message = (form.elements.namedItem("message") as HTMLTextAreaElement)
			.value;
		if (message.length > 500) return;

		setStatus("sending");
		const data = new URLSearchParams();
		const formData = new FormData(form);
		formData.forEach((value, key) => data.append(key, value as string));

		try {
			const [res] = await Promise.allSettled([
				fetch(FORM_URL, {
					method: "POST",
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
					body: data.toString(),
				}),
				fetch(FORMSPREE_URL, {
					method: "POST",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: data.toString(),
				}),
			]);
			if (res.status === "fulfilled" && res.value.ok) {
				setStatus("success");
				form.reset();
				setCharCount(0);
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	return (
		<section id="contact" className="bg-navy-950 py-12 px-4 sm:px-6 md:py-24">
			<div className="max-w-7xl mx-auto">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
					{/* Left */}
					<div>
						<span className="inline-block text-electric text-xs sm:text-sm font-semibold tracking-widest uppercase font-body mb-3">
							{t("contact_label")}
						</span>
						<h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-5 leading-tight">
							{t("contact_title_1")}
							<br />
							<span className="text-electric">{t("contact_title_2")}</span>
						</h2>
						<p className="font-body text-white/60 text-sm md:text-base leading-relaxed mb-6 md:mb-10">
							{t("contact_sub")}
						</p>

						<div className="space-y-3 md:space-y-4">
							{/* WhatsApp card */}
							<div className="bg-navy-800 border border-navy-600 rounded-xl p-4 md:p-5">
								<div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
									<div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-[#25D366]/15 text-[#25D366] flex-shrink-0">
										<i className="ri-whatsapp-line text-xl md:text-2xl"></i>
									</div>
									<div>
										<div className="font-heading text-sm font-bold text-white mb-0.5">
											{t("contact_label")}
										</div>
										<div className="font-body text-xs text-white/50">
											{t("contact_whatsapp_sub")}
										</div>
									</div>
								</div>
								<div className="flex flex-col gap-2 pl-1">
									<a
										href={WHATSAPP_DR}
										target="_blank"
										rel="noopener noreferrer nofollow"
										className="flex items-center justify-between bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/20 rounded-lg px-3 md:px-4 py-2.5 cursor-pointer transition-all duration-200 group"
									>
										<span className="font-body text-sm font-semibold text-white/80">
											(809) 538-8899
										</span>
										<div className="w-4 h-4 flex items-center justify-center text-[#25D366] group-hover:translate-x-0.5 transition-transform">
											<i className="ri-arrow-right-line text-sm"></i>
										</div>
									</a>
								</div>
							</div>

							<div className="flex items-center gap-3 md:gap-4 bg-navy-800 border border-navy-600 rounded-xl p-4 md:p-5">
								<div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-electric/15 text-electric flex-shrink-0">
									<i className="ri-mail-line text-xl md:text-2xl"></i>
								</div>
								<div>
									<div className="font-heading text-sm font-bold text-white mb-0.5">
										{t("contact_email_title")}
									</div>
									<div className="font-body text-xs text-white/50 break-all">
										innoflextechnology@innoflexapp.com
									</div>
								</div>
							</div>

							<div className="flex items-center gap-3 md:gap-4 bg-navy-800 border border-navy-600 rounded-xl p-4 md:p-5">
								<div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-electric/15 text-electric flex-shrink-0">
									<i className="ri-map-pin-2-line text-xl md:text-2xl"></i>
								</div>
								<div>
									<div className="font-heading text-sm font-bold text-white mb-0.5">
										{t("contact_address_title")}
									</div>
									<div className="font-body text-xs text-white/50 leading-relaxed">
										C/Cervantes No. 203, Local 2-C, Gazcue,
										<br />
										Sto. Dgo, D.N — República Dominicana
									</div>
								</div>
							</div>

							<a
								href="https://www.instagram.com/innoflextechnology?igsh=YmRtb2FyemVldm40"
								target="_blank"
								rel="noopener noreferrer nofollow"
								className="flex items-center gap-3 md:gap-4 bg-navy-800 border border-navy-600 hover:border-pink-500/40 rounded-xl p-4 md:p-5 cursor-pointer transition-all duration-200 group"
							>
								<div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-pink-500/15 text-pink-400 flex-shrink-0 group-hover:bg-pink-500/25 transition-colors">
									<i className="ri-instagram-line text-xl md:text-2xl"></i>
								</div>
								<div>
									<div className="font-heading text-sm font-bold text-white mb-0.5">
										Instagram
									</div>
									<div className="font-body text-xs text-white/50">
										@innoflextechnology
									</div>
								</div>
								<div className="ml-auto w-5 h-5 flex items-center justify-center text-white/30 group-hover:text-pink-400 transition-colors">
									<i className="ri-arrow-right-up-line text-base"></i>
								</div>
							</a>
						</div>
					</div>

					{/* Right — Form */}
					<div className="bg-navy-800 border border-navy-600 rounded-2xl p-5 sm:p-7 md:p-8">
						<h3 className="font-heading text-lg md:text-xl font-bold text-white mb-5 md:mb-6">
							{t("contact_form_title")}
						</h3>

						{status === "success" ? (
							<div className="text-center py-10 md:py-12">
								<div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-electric/15 text-electric mx-auto mb-4">
									<i className="ri-check-double-line text-2xl md:text-3xl"></i>
								</div>
								<h4 className="font-heading text-lg font-bold text-white mb-2">
									{t("contact_success_title")}
								</h4>
								<p className="font-body text-white/60 text-sm">
									{t("contact_success_sub")}
								</p>
							</div>
						) : (
							<form
								data-readdy-form
								id="innoflex-contact"
								onSubmit={handleSubmit}
								className="space-y-4"
							>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div>
										<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
											{t("contact_form_name")} *
										</label>
										<input
											type="text"
											name="name"
											required
											placeholder={t("contact_ph_name")}
											className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors placeholder-white/25"
										/>
									</div>
									<div>
										<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
											{t("contact_form_phone")} *
										</label>
										<input
											type="tel"
											name="phone"
											required
											placeholder={t("contact_ph_phone")}
											className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors placeholder-white/25"
										/>
									</div>
								</div>

								<div>
									<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
										{t("contact_form_email")} *
									</label>
									<input
										type="email"
										name="email"
										required
										placeholder={t("contact_ph_email")}
										className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors placeholder-white/25"
									/>
								</div>

								<div>
									<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
										{t("contact_form_type")} *
									</label>
									<select
										name="business_type"
										required
										defaultValue=""
										className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors cursor-pointer"
									>
										<option value="" disabled className="text-white/40">
											{t("contact_select_placeholder")}
										</option>
										<option value="Restaurante">
											{t("contact_ind_restaurant")}
										</option>
										<option value="Supermercado">
											{t("contact_ind_supermarket")}
										</option>
										<option value="Ferretería">
											{t("contact_ind_hardware")}
										</option>
										<option value="Minimarket / Colmado">
											{t("contact_ind_minimarket")}
										</option>
										<option value="Distribuidora / Dealer">
											{t("contact_ind_distributor")}
										</option>
										<option value="Agencia de Préstamos">
											{t("contact_ind_loan")}
										</option>
										<option value="Otro">{t("contact_ind_other")}</option>
									</select>
								</div>

								<div>
									<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
										{t("contact_form_request")} *
									</label>
									<select
										name="request_type"
										required
										defaultValue=""
										className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors cursor-pointer"
									>
										<option value="" disabled className="text-white/40">
											{t("contact_req_placeholder")}
										</option>
										<option value="Demo del Sistema">
											{t("contact_req_demo")}
										</option>
										<option value="Consultoría Tecnológica">
											{t("contact_req_consulting")}
										</option>
										<option value="Paquete Completo con FEL - $450">
											{t("contact_req_full_bundle")}
										</option>
										<option value="Solo Certificación FEL - $300">
											{t("contact_req_fel_only")}
										</option>
										<option value="Proyecto a la Medida">
											{t("contact_req_project")}
										</option>
										<option value="Página Web">
											{t("contact_req_website")}
										</option>
										<option value="Otro">{t("contact_req_other")}</option>
									</select>
								</div>

								<div>
									<label className="font-body text-xs font-semibold text-white/60 mb-1.5 block">
										{t("contact_form_msg")}
										<span
											className={`ml-auto float-right ${charCount > 450 ? "text-yellow-400" : "text-white/30"}`}
										>
											{charCount}/500
										</span>
									</label>
									<textarea
										name="message"
										rows={4}
										maxLength={500}
										placeholder={t("contact_ph_msg")}
										onChange={(e) => setCharCount(e.target.value.length)}
										className="w-full bg-navy-900 border border-navy-600 focus:border-electric text-white text-sm font-body px-3 md:px-4 py-3 rounded-lg outline-none transition-colors placeholder-white/25 resize-none"
									/>
								</div>

								{status === "error" && (
									<p className="text-red-400 text-xs font-body">
										{t("contact_error")}
									</p>
								)}

								<button
									type="submit"
									disabled={status === "sending"}
									className="w-full bg-electric hover:bg-electric/80 text-navy-950 font-bold text-sm font-body py-4 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
								>
									{status === "sending"
										? t("contact_form_sending")
										: t("contact_form_send")}
								</button>
							</form>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
